import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getCharacters,
  deleteCharacter,
  adminListMediaJobs,
  adminListAssets,
  getProviders,
  adminSetProviderStatus,
  getSystemSettings,
  adminSetSetting,
  getRiskItems,
  type DbCharacter,
  type DbMediaJob,
  type DbAsset,
  type DbProvider,
  type DbSystemSetting,
  type RiskItem,
} from '@/api/astraea';
import {
  getCreditCosts,
  adminListMembers,
  adminSetCredits,
  adminCreateMember,
  adminAdjustCredits,
  adminSetCost,
  adminResetPassword,
  adminRecentActivity,
  adminRecentLogins,
  adminTotalCredits,
  adminUsageSummary,
  adminSetMoneyCost,
  getFallbackAvatars,
  addFallbackAvatar,
  deleteFallbackAvatar,
  type CreditCost,
  type AdminMember,
  type AdminActivity,
  type AdminLogin,
  type UsageRow,
  type FallbackAvatar,
} from '@/api/auth';

type Tab = 'dashboard' | 'members' | 'roles' | 'jobs' | 'assets' | 'costs' | 'usage' | 'fallback' | 'providers' | 'risk' | 'settings' | 'activity' | 'logins';

const PLAT_LABEL: Record<string, string> = { xiaohongshu: '小红书', douyin: '抖音', kuaishou: '快手', bilibili: 'B站', wechat_channels: '视频号', zhihu: '知乎', wb58: '58同城' };
const JOB_TYPE_LABEL: Record<string, string> = { text_to_image: '文生图', image_to_image: '图生图', text_to_video: '文生影片', image_to_video: '图生影片', talking_avatar: '口播', voice_generation: '配音' };
const JOB_STATUS: Record<string, string> = { pending: 'bg-secondary-100 text-secondary-500', processing: 'bg-blue-50 text-blue-700', completed: 'bg-green-50 text-green-700', failed: 'bg-red-50 text-red-700', timeout: 'bg-red-50 text-red-700' };

const FB_STYLES = ['温柔陪伴', '清醒知性', '职场专业', '生活审美', '亲和接地气', '成熟稳重'];
const FB_AGES = ['20-25', '25-30', '30-40', '40+'];
const FB_SCENES = ['居家', '办公室', '咖啡店', '街头', '直播间'];

export default function AdminPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [memberSearch, setMemberSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newCredits, setNewCredits] = useState('100');
  const [newBusy, setNewBusy] = useState(false);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [costs, setCosts] = useState<CreditCost[]>([]);
  const [activity, setActivity] = useState<AdminActivity[]>([]);
  const [logins, setLogins] = useState<AdminLogin[]>([]);
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [totalCredits, setTotalCredits] = useState<number>(0);
  const [fallbacks, setFallbacks] = useState<FallbackAvatar[]>([]);
  const [allChars, setAllChars] = useState<DbCharacter[]>([]);
  const [mediaJobs, setMediaJobs] = useState<DbMediaJob[]>([]);
  const [assets, setAssets] = useState<DbAsset[]>([]);
  const [memberDetail, setMemberDetail] = useState<AdminMember | null>(null);
  const [providers, setProviders] = useState<DbProvider[]>([]);
  const [settings, setSettings] = useState<DbSystemSetting[]>([]);
  const [riskItems, setRiskItems] = useState<RiskItem[]>([]);
  const [fbGender, setFbGender] = useState<'female' | 'male'>('female');
  const [fbStyle, setFbStyle] = useState(FB_STYLES[0]);
  const [fbAge, setFbAge] = useState(FB_AGES[1]);
  const [fbScene, setFbScene] = useState('');
  const [fbUploading, setFbUploading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [deletingCharId, setDeletingCharId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      const [m, c, a, l, u, tc, fb, ch, mj, ast] = await Promise.all([
        adminListMembers().catch(() => []),
        getCreditCosts().catch(() => []),
        adminRecentActivity(100).catch(() => []),
        adminRecentLogins(100).catch(() => []),
        adminUsageSummary(30).catch(() => []),
        adminTotalCredits().catch(() => 0),
        getFallbackAvatars().catch(() => []),
        getCharacters().catch(() => []),
        adminListMediaJobs(100).catch(() => []),
        adminListAssets(150).catch(() => []),
      ]);
      setMembers(m); setCosts(c); setActivity(a); setLogins(l); setUsage(u); setTotalCredits(tc); setFallbacks(fb);
      setAllChars(ch); setMediaJobs(mj); setAssets(ast);
      getProviders().then(setProviders).catch(() => {});
      getSystemSettings().then(setSettings).catch(() => {});
      getRiskItems(80).then(setRiskItems).catch(() => {});
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '读取失败');
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const flash = (t: string) => { setMsg(t); setTimeout(() => setMsg(null), 2500); };

  const handleDeleteCharacter = async (c: DbCharacter) => {
    if (!window.confirm(`确定删除角色「${c.name}」？策略、任务与素材将一并删除,已花费的点数不会退回。`)) return;
    setDeletingCharId(c.id);
    try {
      await deleteCharacter(c.id);
      flash(`已删除角色「${c.name}」`);
      reload();
    } catch (e) {
      flash(e instanceof Error ? e.message : '删除失败');
    } finally {
      setDeletingCharId(null);
    }
  };

  const handleCreateMember = async () => {
    const email = newEmail.trim();
    if (!email || newPw.length < 6) { flash('请填 Email 与至少 6 位密码'); return; }
    setNewBusy(true);
    try {
      await adminCreateMember(email, newPw, parseInt(newCredits, 10) || 0);
      flash(`已新增会员 ${email}`);
      setNewOpen(false); setNewEmail(''); setNewPw(''); setNewCredits('100');
      reload();
    } catch (e) { flash(e instanceof Error ? e.message : '新增失败'); }
    finally { setNewBusy(false); }
  };

  const handleSet = async (m: AdminMember) => {
    const v = window.prompt(`设定 ${m.email} 的点数为:`, String(m.balance));
    if (v === null) return;
    const n = parseInt(v, 10);
    if (Number.isNaN(n)) return;
    try { await adminSetCredits(m.user_id, n); flash(`已将 ${m.email} 设为 ${n} 点`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleAdjust = async (m: AdminMember, delta: number) => {
    try { await adminAdjustCredits(m.user_id, delta, 'admin panel'); flash(`${m.email} ${delta > 0 ? '+' : ''}${delta} 点`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleResetPw = async (m: AdminMember) => {
    const v = window.prompt(`为 ${m.email} 设定新密码(至少 8 位,需含字母与数字):`, '');
    if (!v) return;
    try { await adminResetPassword(m.user_id, v); flash(`已重设 ${m.email} 的密码`); }
    catch (e) { flash(e instanceof Error ? e.message : '重设失败'); }
  };

  const handleCost = async (c: CreditCost) => {
    const v = window.prompt(`「${c.label || c.action}」每次扣几点:`, String(c.cost));
    if (v === null) return;
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 0) return;
    try { await adminSetCost(c.action, n); flash(`${c.label || c.action} 已设为 ${n} 点`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleMoneyCost = async (u: UsageRow) => {
    const v = window.prompt(`「${u.label}」每次的实际成本(元):`, String(u.money_cost));
    if (v === null) return;
    const n = parseFloat(v);
    if (Number.isNaN(n) || n < 0) return;
    try { await adminSetMoneyCost(u.action, n); flash(`${u.label} 成本设为 ¥${n}`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleFbUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFbUploading(true);
    try {
      await addFallbackAvatar(file, { gender: fbGender, style: fbStyle, age_range: fbAge, scene: fbScene || undefined });
      flash('已上传兜底形象'); reload();
    } catch (e2) { flash(e2 instanceof Error ? e2.message : '上传失败'); }
    finally { setFbUploading(false); e.target.value = ''; }
  };

  const handleToggleProvider = async (p: DbProvider) => {
    const next = p.status === 'active' ? 'inactive' : 'active';
    try { await adminSetProviderStatus(p.provider_key, next); flash(`${p.provider_name} 已${next === 'active' ? '启用' : '停用'}`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleEditSetting = async (s: DbSystemSetting) => {
    const cur = typeof s.value === 'string' ? s.value : JSON.stringify(s.value);
    const v = window.prompt(`修改「${s.label || s.key}」:`, cur);
    if (v === null) return;
    let parsed: unknown = v;
    try { parsed = JSON.parse(v); } catch { parsed = v; } // 非 JSON 就当字串
    try { await adminSetSetting(s.key, parsed); flash(`已更新 ${s.label || s.key}`); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '失败'); }
  };

  const handleFbDelete = async (id: number) => {
    if (!window.confirm('删除这张兜底形象?')) return;
    try { await deleteFallbackAvatar(id); flash('已删除'); reload(); }
    catch (e) { flash(e instanceof Error ? e.message : '删除失败'); }
  };

  const totalEstMoney = usage.reduce((s, u) => s + Number(u.est_money || 0), 0);
  const total30dJobs = usage.reduce((s, u) => s + Number(u.cnt || 0), 0);
  const total30dPoints = usage.reduce((s, u) => s + Number(u.points || 0), 0);
  const weekAgo = Date.now() - 7 * 86400000;
  const activeMembers = members.filter((m) => m.last_sign_in_at && new Date(m.last_sign_in_at).getTime() > weekAgo).length;
  const fbFiltered = fallbacks.filter((f) => (fbGender === 'female' ? f.gender === 'female' : f.gender !== 'female'));
  const membersFiltered = memberSearch.trim() ? members.filter((m) => (m.email || '').toLowerCase().includes(memberSearch.trim().toLowerCase())) : members;
  const emailById: Record<string, string> = {}; for (const m of members) emailById[m.user_id] = m.email;
  const charById: Record<number, string> = {}; for (const c of allChars) charById[c.id] = c.name;
  const detailChars = memberDetail ? allChars.filter((c) => c.owner_id === memberDetail.user_id) : [];
  const detailActivity = memberDetail ? activity.filter((a) => a.email === memberDetail.email).slice(0, 12) : [];

  const tabs: { key: Tab; label: string; icon: string }[] = [
    { key: 'dashboard', label: 'Dashboard 总览', icon: 'ri-dashboard-3-line' },
    { key: 'members', label: '会员 / 点数', icon: 'ri-team-line' },
    { key: 'roles', label: '角色管理', icon: 'ri-user-star-line' },
    { key: 'jobs', label: '生成中心任务', icon: 'ri-stack-line' },
    { key: 'assets', label: '内容 / 素材库', icon: 'ri-folder-image-line' },
    { key: 'costs', label: '功能点数设定', icon: 'ri-coins-line' },
    { key: 'usage', label: '用量 / 成本', icon: 'ri-money-cny-circle-line' },
    { key: 'fallback', label: '兜底形象库', icon: 'ri-image-add-line' },
    { key: 'providers', label: '供应商 / API', icon: 'ri-plug-line' },
    { key: 'risk', label: '风控 / 审核', icon: 'ri-shield-check-line' },
    { key: 'settings', label: '系统设定', icon: 'ri-settings-3-line' },
    { key: 'activity', label: '操作纪录', icon: 'ri-history-line' },
    { key: 'logins', label: '登入地点', icon: 'ri-map-pin-line' },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
        <Link to="/admin/reliability" className="hover:text-foreground-600">平台稳定控制台</Link>
        <i className="ri-arrow-right-s-line"></i><span>完整营运后台</span>
      </div>
      <h1 className="text-2xl font-bold text-foreground-950 mb-1">营运管理中心</h1>
      <p className="text-sm text-foreground-600 mb-6">会员与点数、功能成本与毛利、用量分析、兜底形象、操作与登入安全。</p>

      {msg && <div className="mb-4 p-3 rounded-lg bg-primary-50 border border-primary-200 text-sm text-primary-700">{msg}</div>}

      <div className="flex flex-col md:flex-row gap-5">
        {/* 左侧 Sidebar */}
        <nav className="md:w-48 flex-shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-visible">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap md:w-full ${tab === t.key ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-secondary-100'}`}>
              <i className={t.icon}></i>{t.label}
            </button>
          ))}
        </nav>

        {/* 右侧内容 */}
        <div className="flex-1 min-w-0">

      {tab === 'dashboard' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4"><div className="text-[11px] text-secondary-400">会员数</div><div className="text-2xl font-bold text-foreground-950 mt-1">{members.length}</div></div>
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4"><div className="text-[11px] text-secondary-400">近 7 天活跃会员</div><div className="text-2xl font-bold text-foreground-950 mt-1">{activeMembers}</div></div>
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4"><div className="text-[11px] text-secondary-400">会员点数总额</div><div className="text-2xl font-bold text-primary-700 mt-1">{totalCredits}</div></div>
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4"><div className="text-[11px] text-secondary-400">30 天生成次数</div><div className="text-2xl font-bold text-foreground-950 mt-1">{total30dJobs}</div></div>
            <div className="rounded-xl border border-secondary-200 bg-background-50 p-4"><div className="text-[11px] text-secondary-400">30 天消耗点数</div><div className="text-2xl font-bold text-foreground-950 mt-1">{total30dPoints}</div></div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4"><div className="text-[11px] text-amber-700">30 天估算 API 成本</div><div className="text-2xl font-bold text-amber-700 mt-1">¥ {totalEstMoney.toFixed(2)}</div></div>
          </div>
          <div className="rounded-xl border border-secondary-200 bg-background-50 p-4">
            <div className="text-xs font-semibold text-foreground-800 mb-2">功能用量排行(近 30 天)</div>
            {usage.length === 0 ? <div className="text-xs text-secondary-400 py-4 text-center">近 30 天尚无用量</div> : (
              <div className="space-y-1.5">
                {[...usage].sort((a, b) => Number(b.cnt) - Number(a.cnt)).slice(0, 6).map((u) => {
                  const max = Math.max(...usage.map((x) => Number(x.cnt) || 0), 1);
                  return (
                    <div key={u.action} className="flex items-center gap-2 text-xs">
                      <span className="w-20 truncate text-foreground-700">{u.label}</span>
                      <div className="flex-1 h-2 rounded-full bg-secondary-100 overflow-hidden"><div className="h-full bg-primary-500" style={{ width: `${Math.round((Number(u.cnt) / max) * 100)}%` }}></div></div>
                      <span className="w-10 text-right text-secondary-500">{u.cnt}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'members' && (
        <div className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 max-w-xs min-w-[160px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400 text-sm"></i>
            <input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="搜寻 Email" className="w-full pl-8 pr-3 py-2 rounded-lg border border-secondary-200 bg-background-50 text-sm focus:outline-none focus:border-primary-400" />
          </div>
          <span className="text-xs text-secondary-400">{membersFiltered.length} / {members.length} 位</span>
          <button onClick={() => setNewOpen((v) => !v)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer"><i className="ri-user-add-line"></i>新增会员</button>
        </div>
        {newOpen && (
          <div className="rounded-xl border border-primary-200 bg-primary-50/40 p-4 flex flex-wrap items-end gap-3">
            <div><div className="text-[11px] text-secondary-500 mb-1">Email</div><input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="member@example.com" className="text-sm border border-secondary-200 rounded-lg px-3 py-1.5 bg-background-50 w-56" /></div>
            <div><div className="text-[11px] text-secondary-500 mb-1">密码(至少 6 位)</div><input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="密码" className="text-sm border border-secondary-200 rounded-lg px-3 py-1.5 bg-background-50 w-40" /></div>
            <div><div className="text-[11px] text-secondary-500 mb-1">初始点数</div><input value={newCredits} onChange={(e) => setNewCredits(e.target.value)} className="text-sm border border-secondary-200 rounded-lg px-3 py-1.5 bg-background-50 w-24" /></div>
            <button onClick={handleCreateMember} disabled={newBusy} className={`px-4 py-1.5 rounded-lg text-xs font-semibold text-background-50 ${newBusy ? 'bg-primary-400 cursor-wait' : 'bg-primary-500 hover:bg-primary-600 cursor-pointer'}`}>{newBusy ? '建立中…' : '建立帐号'}</button>
            <button onClick={() => setNewOpen(false)} className="px-3 py-1.5 rounded-lg text-xs border border-secondary-200 text-foreground-600 cursor-pointer">取消</button>
          </div>
        )}
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">Email</th><th className="text-right px-4 py-2.5">点数</th><th className="text-left px-4 py-2.5 hidden sm:table-cell">最后登入</th><th className="text-right px-4 py-2.5">生杀操作</th></tr>
            </thead>
            <tbody>
              {membersFiltered.map((m) => (
                <tr key={m.user_id} className="border-t border-secondary-100">
                  <td className="px-4 py-2.5"><button onClick={() => setMemberDetail(m)} className="text-foreground-800 hover:text-primary-600 hover:underline cursor-pointer text-left">{m.email}</button></td>
                  <td className="px-4 py-2.5 text-right font-semibold text-primary-700">{m.balance}</td>
                  <td className="px-4 py-2.5 text-xs text-secondary-400 hidden sm:table-cell">{m.last_sign_in_at ? new Date(m.last_sign_in_at).toLocaleString() : '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => handleAdjust(m, -10)} className="w-7 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer" title="-10">−10</button>
                      <button onClick={() => handleAdjust(m, 50)} className="px-2 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer" title="+50">+50</button>
                      <button onClick={() => handleSet(m)} className="px-2.5 h-7 rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer text-xs">设定</button>
                      <button onClick={() => handleResetPw(m)} className="px-2.5 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer text-xs" title="重设密码"><i className="ri-lock-password-line"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
              {membersFiltered.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-secondary-400 text-xs">{members.length === 0 ? '尚无会员' : '查无符合的会员'}</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-secondary-400">点 Email 可查看该会员详情(角色、近期消耗)。</p>
        </div>
      )}

      {tab === 'roles' && (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">角色</th><th className="text-left px-4 py-2.5">所属会员</th><th className="text-left px-4 py-2.5 hidden sm:table-cell">平台</th><th className="text-left px-4 py-2.5">声音</th><th className="text-left px-4 py-2.5">操作</th></tr>
            </thead>
            <tbody>
              {allChars.map((c) => (
                <tr key={c.id} className="border-t border-secondary-100">
                  <td className="px-4 py-2.5 text-foreground-800">{c.name}{c.avatar_url ? ' ' : ''}</td>
                  <td className="px-4 py-2.5 text-xs text-secondary-500">{c.owner_id ? (emailById[c.owner_id] || '—') : '未认领'}</td>
                  <td className="px-4 py-2.5 hidden sm:table-cell"><span className="flex flex-wrap gap-1">{(c.target_platforms || []).map((p) => <span key={p} className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[10px]">{PLAT_LABEL[p] || p}</span>)}</span></td>
                  <td className="px-4 py-2.5 text-xs text-secondary-500">{c.voice_id ? '已绑定' : '未绑定'}</td>
                  <td className="px-4 py-2.5"><Link to={`/board/${c.id}`} className="text-[11px] text-primary-600 hover:underline">进任务板</Link>
                    <button type="button" onClick={() => handleDeleteCharacter(c)} disabled={deletingCharId === c.id} className="ml-2 text-[11px] text-red-600 hover:underline cursor-pointer disabled:opacity-50">{deletingCharId === c.id ? '删除中…' : '删除'}</button></td>
                </tr>
              ))}
              {allChars.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-secondary-400 text-xs">尚无角色</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'jobs' && (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">类型</th><th className="text-left px-4 py-2.5">会员</th><th className="text-left px-4 py-2.5">供应商</th><th className="text-left px-4 py-2.5">状态</th><th className="text-right px-4 py-2.5">扣点</th><th className="text-left px-4 py-2.5 hidden sm:table-cell">时间</th></tr>
            </thead>
            <tbody>
              {mediaJobs.map((j) => (
                <tr key={j.id} className="border-t border-secondary-100">
                  <td className="px-4 py-2 text-foreground-800">{JOB_TYPE_LABEL[j.job_type] || j.job_type}</td>
                  <td className="px-4 py-2 text-xs text-secondary-500">{j.user_id ? (emailById[j.user_id] || j.user_id.slice(0, 8)) : '—'}</td>
                  <td className="px-4 py-2 text-xs text-secondary-500">{j.provider || '—'}</td>
                  <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${JOB_STATUS[j.status] || 'bg-secondary-100 text-secondary-500'}`}>{j.status}</span>{j.error ? <span className="block text-[10px] text-red-500 mt-0.5 max-w-[160px] truncate">{j.error}</span> : null}</td>
                  <td className="px-4 py-2 text-right text-secondary-500">{j.cost_points}</td>
                  <td className="px-4 py-2 text-xs text-secondary-400 hidden sm:table-cell">{new Date(j.created_at).toLocaleString()}</td>
                </tr>
              ))}
              {mediaJobs.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-secondary-400 text-xs">尚无生成中心任务</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'assets' && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
          {assets.map((a) => (
            <div key={a.id} className="rounded-xl overflow-hidden border border-secondary-200 bg-background-100">
              <div className="aspect-square bg-secondary-100 flex items-center justify-center">
                {a.status === 'ready' && a.url ? (
                  a.asset_type === 'video' ? <video src={a.url} className="w-full h-full object-cover" /> : a.asset_type === 'audio' ? <i className="ri-mic-line text-2xl text-secondary-400"></i> : <img src={a.url} alt="" className="w-full h-full object-cover" />
                ) : <span className="text-[10px] text-secondary-400">{a.status}</span>}
              </div>
              <div className="p-1.5">
                <div className="text-[10px] text-foreground-700">{a.asset_type}{a.character_id ? ` · ${charById[a.character_id] || a.character_id}` : ''}</div>
                <div className="text-[9px] text-secondary-400">{new Date(a.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
          {assets.length === 0 && <div className="col-span-full py-10 text-center text-secondary-400 text-xs">尚无素材</div>}
        </div>
      )}

      {tab === 'costs' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {costs.map((c) => (
            <div key={c.action} className="flex items-center justify-between rounded-xl border border-secondary-200 p-4">
              <div>
                <div className="text-sm font-medium text-foreground-900">{c.label || c.action}</div>
                <div className="text-[11px] text-secondary-400">{c.action}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-primary-700">{c.cost}</span><span className="text-xs text-secondary-400">点</span>
                <button onClick={() => handleCost(c)} className="ml-2 px-2.5 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer text-xs">修改</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'usage' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-secondary-200 p-4">
              <div className="text-[11px] text-secondary-400">会员点数总额(所有会员余额)</div>
              <div className="text-2xl font-bold text-primary-700 mt-1">{totalCredits} 点</div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
              <div className="text-[11px] text-amber-700">本月估算 API 成本(近 30 天)</div>
              <div className="text-2xl font-bold text-amber-700 mt-1">¥ {totalEstMoney.toFixed(2)}</div>
              <div className="text-[10px] text-amber-600/80 mt-1">依各功能「实际成本」× 次数估算,接近额度时请及时储值。</div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-secondary-200">
            <table className="w-full text-sm">
              <thead className="bg-background-100 text-secondary-500 text-xs">
                <tr><th className="text-left px-4 py-2.5">功能</th><th className="text-right px-4 py-2.5">次数(30天)</th><th className="text-right px-4 py-2.5">消耗点数</th><th className="text-right px-4 py-2.5">单次成本</th><th className="text-right px-4 py-2.5">估算花费</th><th className="text-right px-4 py-2.5"></th></tr>
              </thead>
              <tbody>
                {usage.map((u) => (
                  <tr key={u.action} className="border-t border-secondary-100">
                    <td className="px-4 py-2 text-foreground-800">{u.label}</td>
                    <td className="px-4 py-2 text-right">{u.cnt}</td>
                    <td className="px-4 py-2 text-right text-secondary-500">{u.points}</td>
                    <td className="px-4 py-2 text-right">¥{Number(u.money_cost).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right font-medium text-amber-700">¥{Number(u.est_money).toFixed(2)}</td>
                    <td className="px-4 py-2 text-right"><button onClick={() => handleMoneyCost(u)} className="text-[11px] text-primary-600 hover:underline cursor-pointer">改成本</button></td>
                  </tr>
                ))}
                {usage.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-secondary-400 text-xs">近 30 天尚无用量</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'fallback' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-secondary-200 p-4">
            <div><div className="text-[11px] text-secondary-500 mb-1">性别</div>
              <div className="flex gap-1">{(['female','male'] as const).map(g=>(<button key={g} onClick={()=>setFbGender(g)} className={`px-3 py-1.5 rounded-lg text-xs cursor-pointer ${fbGender===g?'bg-primary-500 text-background-50':'border border-secondary-200 text-foreground-600'}`}>{g==='female'?'女':'男'}</button>))}</div>
            </div>
            <div><div className="text-[11px] text-secondary-500 mb-1">风格</div>
              <select value={fbStyle} onChange={e=>setFbStyle(e.target.value)} className="text-xs border border-secondary-200 rounded-lg px-2 py-1.5 bg-background-50">{FB_STYLES.map(s=><option key={s}>{s}</option>)}</select></div>
            <div><div className="text-[11px] text-secondary-500 mb-1">年龄</div>
              <select value={fbAge} onChange={e=>setFbAge(e.target.value)} className="text-xs border border-secondary-200 rounded-lg px-2 py-1.5 bg-background-50">{FB_AGES.map(s=><option key={s}>{s}</option>)}</select></div>
            <div><div className="text-[11px] text-secondary-500 mb-1">场景(选填)</div>
              <select value={fbScene} onChange={e=>setFbScene(e.target.value)} className="text-xs border border-secondary-200 rounded-lg px-2 py-1.5 bg-background-50"><option value="">—</option>{FB_SCENES.map(s=><option key={s}>{s}</option>)}</select></div>
            <label className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium cursor-pointer ${fbUploading?'bg-primary-400 text-background-50 cursor-wait':'bg-primary-500 text-background-50 hover:bg-primary-600'}`}>
              <i className="ri-upload-2-line"></i>{fbUploading?'上传中…':'上传形象图'}
              <input type="file" accept="image/*" className="hidden" disabled={fbUploading} onChange={handleFbUpload} />
            </label>
          </div>
          <div className="flex gap-2">
            {(['female','male'] as const).map(g=>(<button key={g} onClick={()=>setFbGender(g)} className={`px-4 py-1.5 rounded-full text-xs font-medium cursor-pointer ${fbGender===g?'bg-primary-500 text-background-50':'border border-secondary-200 text-foreground-600'}`}>{g==='female'?'女声形象':'男性形象'}({fallbacks.filter(f=>g==='female'?f.gender==='female':f.gender!=='female').length})</button>))}
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
            {fbFiltered.map(f=>(
              <div key={f.id} className="group relative rounded-xl overflow-hidden border border-secondary-200 bg-background-100">
                <img src={f.url} alt={f.label||''} className={`w-full aspect-[3/4] object-cover ${f.claimed_by?'opacity-40':''}`} />
                {f.claimed_by && <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-secondary-700/80 text-background-50 text-[9px]">已被认领</div>}
                <div className="absolute bottom-0 inset-x-0 bg-black/45 text-background-50 text-[9px] px-1.5 py-1 truncate">{f.style||''}{f.age_range?(' · '+f.age_range):''}</div>
                <button onClick={()=>handleFbDelete(f.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-background-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center"><i className="ri-delete-bin-line text-xs"></i></button>
              </div>
            ))}
            {fbFiltered.length===0 && <div className="col-span-full py-10 text-center text-secondary-400 text-xs">此分类尚无兜底形象,点上方「上传形象图」新增。</div>}
          </div>
          <p className="text-[10px] text-secondary-400">所有图以统一 3:4 置中裁切显示,看起来尺寸一致。</p>
        </div>
      )}

      {tab === 'providers' && (
        <div className="space-y-3">
          {providers.map((p) => {
            const jobs = mediaJobs.filter((j) => j.provider === p.provider_key);
            const total = jobs.length;
            const ok = jobs.filter((j) => j.status === 'completed').length;
            const rate = total ? Math.round((ok / total) * 100) : null;
            return (
              <div key={p.provider_key} className="rounded-xl border border-secondary-200 p-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-900">{p.provider_name}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${p.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-secondary-100 text-secondary-500'}`}>{p.status === 'active' ? '启用中' : '已停用'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1.5">{(p.supported_features || []).map((f) => <span key={f} className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-600 text-[10px]">{JOB_TYPE_LABEL[f] || f}</span>)}</div>
                  {p.note && <div className="text-[11px] text-secondary-400 mt-1">{p.note}</div>}
                  <div className="text-[11px] text-secondary-500 mt-1">近 100 笔生成成功率:{rate === null ? '—' : `${rate}%(${ok}/${total})`}{p.fallback_provider ? ` · 备援:${p.fallback_provider}` : ''}</div>
                </div>
                <button onClick={() => handleToggleProvider(p)} className="flex-shrink-0 px-2.5 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer text-xs">{p.status === 'active' ? '停用' : '启用'}</button>
              </div>
            );
          })}
          {providers.length === 0 && <div className="py-10 text-center text-secondary-400 text-xs">尚无供应商</div>}
        </div>
      )}

      {tab === 'risk' && (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">时间</th><th className="text-left px-4 py-2.5">内容标题</th><th className="text-left px-4 py-2.5">风险</th><th className="text-left px-4 py-2.5">命中原因</th></tr>
            </thead>
            <tbody>
              {riskItems.map((r) => (
                <tr key={r.id} className="border-t border-secondary-100">
                  <td className="px-4 py-2 text-xs text-secondary-400">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-foreground-800 max-w-[220px] truncate">{r.title || '—'}</td>
                  <td className="px-4 py-2"><span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${r.risk_level === 'high' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>{r.risk_level === 'high' ? '高风险' : '中度风险'}</span></td>
                  <td className="px-4 py-2 text-[11px] text-red-600 max-w-[280px]">{(r.risk_issues || []).join('、') || '—'}</td>
                </tr>
              ))}
              {riskItems.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-secondary-400 text-xs">目前没有中/高风险内容,系统会自动标记需注意的生成内容。</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'settings' && (
        <div className="space-y-2">
          {settings.map((s) => (
            <div key={s.key} className="flex items-center justify-between rounded-xl border border-secondary-200 p-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground-900">{s.label || s.key}</div>
                <div className="text-[11px] text-secondary-400">{s.key}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-700 max-w-[200px] truncate">{typeof s.value === 'string' ? s.value : JSON.stringify(s.value)}</span>
                <button onClick={() => handleEditSetting(s)} className="px-2.5 h-7 rounded-md border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer text-xs">修改</button>
              </div>
            </div>
          ))}
          {settings.length === 0 && <div className="py-10 text-center text-secondary-400 text-xs">尚无系统设定</div>}
        </div>
      )}

      {tab === 'activity' && (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">时间</th><th className="text-left px-4 py-2.5">会员</th><th className="text-left px-4 py-2.5">操作</th><th className="text-right px-4 py-2.5">点数</th></tr>
            </thead>
            <tbody>
              {activity.map((a, i) => (
                <tr key={i} className="border-t border-secondary-100">
                  <td className="px-4 py-2 text-xs text-secondary-400">{new Date(a.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-foreground-700">{a.email || '—'}</td>
                  <td className="px-4 py-2 text-foreground-700">{a.reason || '—'}</td>
                  <td className={`px-4 py-2 text-right font-medium ${a.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>{a.amount > 0 ? '+' : ''}{a.amount}</td>
                </tr>
              ))}
              {activity.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-secondary-400 text-xs">尚无纪录</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'logins' && (
        <div className="overflow-x-auto rounded-xl border border-secondary-200">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-secondary-500 text-xs">
              <tr><th className="text-left px-4 py-2.5">时间</th><th className="text-left px-4 py-2.5">会员</th><th className="text-left px-4 py-2.5">IP</th><th className="text-left px-4 py-2.5">登入地点</th></tr>
            </thead>
            <tbody>
              {logins.map((l, i) => (
                <tr key={i} className="border-t border-secondary-100">
                  <td className="px-4 py-2 text-xs text-secondary-400">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-foreground-700">{l.email || '—'}</td>
                  <td className="px-4 py-2 text-xs text-foreground-600">{l.ip || '—'}</td>
                  <td className="px-4 py-2 text-foreground-700">{l.location || '—'}</td>
                </tr>
              ))}
              {logins.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-secondary-400 text-xs">尚无登入纪录</td></tr>}
            </tbody>
          </table>
        </div>
      )}
        </div>
      </div>

      {memberDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={() => setMemberDetail(null)}>
          <div className="w-full max-w-lg bg-background-50 rounded-2xl border border-secondary-200 shadow-lg p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-foreground-950">会员详情</h2>
              <button onClick={() => setMemberDetail(null)} className="text-secondary-400 hover:text-foreground-700 cursor-pointer"><i className="ri-close-line"></i></button>
            </div>
            <div className="space-y-1 text-sm">
              <div className="text-foreground-900 font-medium">{memberDetail.email}</div>
              <div className="text-xs text-secondary-500">点数余额:<span className="text-primary-700 font-semibold">{memberDetail.balance}</span> · 最后登入:{memberDetail.last_sign_in_at ? new Date(memberDetail.last_sign_in_at).toLocaleString() : '—'}</div>
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-foreground-800 mb-1.5">角色({detailChars.length})</div>
              {detailChars.length ? (
                <div className="space-y-1">
                  {detailChars.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-xs rounded-lg border border-secondary-200 px-3 py-1.5">
                      <span className="text-foreground-800">{c.name}</span>
                      <span className="flex items-center gap-2">
                        <Link to={`/board/${c.id}`} className="text-[11px] text-primary-600 hover:underline">进任务板</Link>
                        <button type="button" onClick={() => handleDeleteCharacter(c)} disabled={deletingCharId === c.id} className="text-[11px] text-red-600 hover:underline cursor-pointer disabled:opacity-50">{deletingCharId === c.id ? '删除中…' : '删除'}</button>
                      </span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-secondary-400">此会员尚无角色</div>}
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold text-foreground-800 mb-1.5">近期点数纪录</div>
              {detailActivity.length ? (
                <div className="space-y-0.5">
                  {detailActivity.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] text-secondary-600">
                      <span>{new Date(a.created_at).toLocaleString()} · {a.reason || '—'}</span>
                      <span className={a.amount < 0 ? 'text-red-600' : 'text-green-600'}>{a.amount > 0 ? '+' : ''}{a.amount}</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-secondary-400">近期无点数纪录</div>}
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => { handleSet(memberDetail); }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer">设定点数</button>
              <button onClick={() => { handleResetPw(memberDetail); }} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-secondary-200 text-foreground-600 hover:bg-secondary-50 cursor-pointer">重设密码</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
